'use client';

import { Text } from '@lmn/design-system';
import React, { type HTMLAttributes, useMemo } from 'react';
import styled, { css } from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss';

import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';

import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { getHotelCheckinCheckoutDates } from '@/app/components/hotel-detail/common/dates';
import { CheckDateTime, hasCheckTimes } from '@/app/components/hotel-detail/HotelCheckinCheckout';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { CONTENT_HUB_UPLOAD_URL } from '@/app/common/contentHubAssets.ts';
import {
  getCardIconName,
  getPolicyIcon,
  getPolicyTranslationKey,
  isSupportedCardType,
  HOTEL_POLICY_SECTION_ORDER,
} from '@/app/components/hotel-detail/common/staticHotelDataAdapter';
import { Icon } from '@/app/common/icons/Icon';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';

// `isEvo` adapts the section to the Evo hotel-info modal: policy descriptions go from BodySmall to
// Body and the container drops its card treatment entirely (see PoliciesContainer). The legacy
// hotel-detail/static layouts leave it unset and keep BodySmall inside the padded card.
type HotelPagePoliciesSectionProps = HTMLAttributes<HTMLElement> & { isEvo?: boolean };

const PoliciesContainer = styled.div<{ $isEvo: boolean }>`
  /* The card is a hotel-page treatment. In the Evo hotel-info modal (MPH-839) the tab supplies the
     inset and the section title its own bottom gap (see SectionColumn in
     EvoHotelDetailsModalContent), so the modal drops the card whole rather than keeping a radius
     and a background around zero padding, and the subsections stay flush on the sides. */
  @media ${customCss.breakpoints.sm} {
    ${(p) =>
      !p.$isEvo &&
      css`
        border-radius: 8px;
        background-color: white;
        padding: 24px;
      `}
  }

  /* The hotel page is wide enough to put each policy title beside its values from sm, so it brings
     the subsections' own lg switch forward. The modal leaves them stacked until lg. Lazy
     interpolation: these components are declared below this one. */
  ${(p) =>
    !p.$isEvo &&
    css`
      @media ${customCss.breakpoints.sm} {
        ${PolicySection} {
          flex-direction: row;
        }

        ${PolicySectionTitle} {
          padding: 0;
        }

        ${Separator} {
          border-top: 1px solid ${(sp) => sp.theme.commons.colors.gray100};
          margin: 24px 0;
        }
      }
    `}
`;

/* The 290px title column sits beside its values only from lg. On the hotel page PoliciesContainer
   brings that switch forward to sm; the modal is too narrow for both at tablet, so there it stays
   stacked until lg (MPH-839). */
const PolicySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px 48px;

  @media ${customCss.breakpoints.lg} {
    flex-direction: row;
  }
`;

// The h3 names the row; its icon is decorative (Icon's alt defaults to '').
const PolicySectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 290px;
  width: 290px;
  padding: 4px 0;

  @media ${customCss.breakpoints.lg} {
    padding: 0;
  }
`;

const PolicySectionTitleText = styled(Text).attrs({ variant: 'Title5', forceTag: 'h3' })``;

const PolicySectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Separator = styled.hr`
  margin: 8px 0;
  border: 0;

  @media ${customCss.breakpoints.lg} {
    border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
    margin: 24px 0;
  }

  &:last-child {
    display: none;
  }
`;

const CreditCardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const Card = styled.div<{ $id: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 4px;

  ${(p) =>
    p.$id === 'CASH' &&
    css`
      color: ${p.theme.commons.colors.success600};
      background-color: ${p.theme.commons.colors.gray50};
    `}
  ${(p) => (p.$id === 'CASH' ? `padding: 0 8px;` : undefined)}
`;

export default function HotelPagePoliciesSection({ isEvo = false, ...props }: HotelPagePoliciesSectionProps) {
  const { t } = useTranslation();
  const descriptionVariant = isEvo ? 'Body' : 'BodySmall';
  const { data: staticData } = useFetchHotelStatic();
  const { checkin: urlCheckin, checkout: urlCheckout } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const searchParams = getSearchParams();
  const initialParams = useInitialParams();

  // There is no specific section for DEPOSIT policies in the UI, so we merge them into fees to show in the fees section.
  // Extract DEPOSIT from policies and merge with fees.
  const mergedFees = useMemo(() => {
    const depositPolicies = staticData?.policies?.DEPOSIT || [];
    if (!depositPolicies.length) return staticData?.fees;
    return {
      optional: staticData?.fees?.optional || null,
      mandatory: [...(staticData?.fees?.mandatory || []), ...depositPolicies],
    };
  }, [staticData?.policies?.DEPOSIT, staticData?.fees]);

  // Remove DEPOSIT from policies and merge unexpected categories into GENERAL
  const displayPolicies = useMemo((): Record<HotelPolicySection, string[]> | undefined => {
    if (!staticData?.policies) return undefined;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { DEPOSIT, ...policiesWithoutDeposit } = staticData.policies;

    const result = {} as Record<HotelPolicySection, string[]>;
    const unexpectedPolicies: string[] = [];

    // Separate known and unknown policy categories
    Object.entries(policiesWithoutDeposit).forEach(([key, value]) => {
      if (HOTEL_POLICY_SECTION_ORDER.includes(key as HotelPolicySection)) {
        result[key as HotelPolicySection] = value;
      } else {
        // Collect unexpected policies to merge into GENERAL
        unexpectedPolicies.push(...value);
      }
    });

    // Merge unexpected policies into GENERAL section
    if (unexpectedPolicies.length > 0) {
      result.GENERAL = [...(result.GENERAL || []), ...unexpectedPolicies];
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }, [staticData?.policies]);

  // Memoize checkin instructions to avoid recreation on every render
  const checkinInstructions = useMemo(
    (): string[] => [
      ...(staticData?.checkinInformation?.minAge
        ? [t('vacation.select.policies.checkin.min_age', { age: staticData.checkinInformation.minAge.toString() })]
        : []),
      ...(staticData?.checkinInformation?.instructions ?? []),
    ],
    [staticData?.checkinInformation?.minAge, staticData?.checkinInformation?.instructions, t]
  );

  if (!staticData) return null;

  const checkin = offersData?.checkin || urlCheckin;
  const checkout = offersData?.checkout || urlCheckout;

  const hotelDates =
    initialParams.isHotelStaticPage && !searchParams[URLParameters.TRIP_DATES]
      ? undefined
      : getHotelCheckinCheckoutDates(localization.locale, checkin, checkout);

  const { checkinInformation, checkoutInformation } = staticData;

  // travel-api answers `{ from: '', to: '' }` for hotels it holds no times for. That object is
  // truthy, so it used to draw a titled row over an empty value column (MPH-1007): every block
  // below now has to have something to print before it renders.
  const hasCheckinRow =
    Boolean(hotelDates?.checkin) || hasCheckTimes(checkinInformation) || checkinInstructions.length > 0;
  const hasCheckoutRow =
    Boolean(hotelDates?.checkout) ||
    hasCheckTimes(checkoutInformation) ||
    Boolean(checkoutInformation?.instructions?.length);
  const fees = [...(mergedFees?.mandatory ?? []), ...(mergedFees?.optional ?? [])];
  const hasFees = fees.length > 0;
  const hasCheckinInstructions = Boolean(checkinInformation?.specialInstructions);
  const hasCheckoutInstructions = Boolean(checkoutInformation?.specialInstructions);
  const houseRules = staticData.houseRules ?? [];
  const spokenLanguages = staticData.spokenLanguages ?? [];
  const paymentTypes = staticData.paymentTypes ?? [];
  const hasHouseRules = houseRules.length > 0;
  const hasSpokenLanguages = spokenLanguages.length > 0;
  const hasPaymentTypes = paymentTypes.length > 0;

  // And with every block empty the section itself is just a heading, so it goes too. Each flag is
  // used exactly twice — here and on its own block below — so the two cannot drift apart.
  const hasAnyPolicy =
    hasCheckinRow ||
    hasCheckoutRow ||
    hasFees ||
    hasCheckinInstructions ||
    hasCheckoutInstructions ||
    hasHouseRules ||
    Boolean(displayPolicies) ||
    hasSpokenLanguages ||
    hasPaymentTypes;

  if (!hasAnyPolicy) return null;

  const displayNames = new Intl.DisplayNames([localization.lang], { type: 'language' });

  return (
    <HotelPageContentSection
      {...props}
      title={t('vacation.select.policies.title')}
      css={css`
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          background-color: inherit;
        }
      `}
    >
      <PoliciesContainer $isEvo={isEvo}>
        {hasCheckinRow && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Arrows" name="icon-login" size={20} color="#000000" />
                <PolicySectionTitleText>{t('vacation.common.checkin')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <PolicySectionInfo>
                <PolicySectionCheckinCheckoutInfo
                  checkInformation={{ ...checkinInformation, instructions: checkinInstructions }}
                  date={hotelDates?.checkin}
                  descriptionVariant={descriptionVariant}
                />
              </PolicySectionInfo>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasCheckoutRow && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Arrows" name="icon-logout" size={20} color="#000000" />
                <PolicySectionTitleText>{t('vacation.common.checkout')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <PolicySectionInfo>
                <PolicySectionCheckinCheckoutInfo
                  checkInformation={checkoutInformation}
                  date={hotelDates?.checkout}
                  descriptionVariant={descriptionVariant}
                />
              </PolicySectionInfo>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasCheckinInstructions && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Semantics" name="icon-circle-info" size={20} color="iconPrimary" />
                <PolicySectionTitleText>{t('vacation.select.policies.checkin.special_info')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <Text variant={descriptionVariant}>{checkinInformation?.specialInstructions}</Text>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasCheckoutInstructions && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Semantics" name="icon-circle-info" size={20} color="iconPrimary" />
                <PolicySectionTitleText>{t('vacation.select.policies.checkout.special_info')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <Text variant={descriptionVariant}>{checkoutInformation?.specialInstructions}</Text>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasHouseRules && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Services" name="icon-rules" size={20} color="iconPrimary" />
                <PolicySectionTitleText>{t('vacation.select.policies.rules')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <PolicySectionInfo>
                {houseRules.map((rule) => (
                  <Text variant={descriptionVariant} key={rule}>
                    {rule}
                  </Text>
                ))}
              </PolicySectionInfo>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasFees && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="Financial" name="icon-single-coin" size={20} color="iconPrimary" />
                <PolicySectionTitleText>{t('vacation.select.policies.fees')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <PolicySectionInfo>
                {fees.map((fee) => (
                  <Text variant={descriptionVariant} key={fee}>
                    {fee}
                  </Text>
                ))}
              </PolicySectionInfo>
            </PolicySection>
            <Separator />
          </>
        )}
        {displayPolicies &&
          (Object.entries(displayPolicies) as [HotelPolicySection, string[]][])
            .sort(
              ([categoryKeyA], [categoryKeyB]) =>
                HOTEL_POLICY_SECTION_ORDER.indexOf(categoryKeyA) - HOTEL_POLICY_SECTION_ORDER.indexOf(categoryKeyB)
            )
            .map(([categoryKey, categoryPolicies]) => {
              if (!categoryPolicies || categoryPolicies.length === 0) return null;

              const translationKey = getPolicyTranslationKey(categoryKey);
              const policyIcon = getPolicyIcon(categoryKey);

              return (
                <React.Fragment key={categoryKey}>
                  <PolicySection>
                    <PolicySectionTitle>
                      <Icon {...policyIcon} size={20} />
                      <PolicySectionTitleText>{t(translationKey)}</PolicySectionTitleText>
                    </PolicySectionTitle>
                    <PolicySectionInfo>
                      {categoryPolicies.map((policy: string) => (
                        <Text variant={descriptionVariant} key={policy}>
                          {policy}
                        </Text>
                      ))}
                    </PolicySectionInfo>
                  </PolicySection>
                  <Separator />
                </React.Fragment>
              );
            })}
        {hasSpokenLanguages && (
          <>
            <PolicySection>
              <PolicySectionTitle>
                <Icon category="UI Essentials" name="icon-language" size={20} color="iconPrimary" />
                <PolicySectionTitleText>{t('vacation.select.policies.languages')}</PolicySectionTitleText>
              </PolicySectionTitle>
              <PolicySectionInfo>
                <Text variant={descriptionVariant}>
                  {spokenLanguages.map((language) => getLanguageName(displayNames, language)).join(', ')}
                </Text>
              </PolicySectionInfo>
            </PolicySection>
            <Separator />
          </>
        )}
        {hasPaymentTypes && (
          <PolicySection>
            <PolicySectionTitle>
              <Icon category="Financial" name="icon-credit-card" size={20} color="iconPrimary" />
              <PolicySectionTitleText>{t('vacation.select.policies.payment')}</PolicySectionTitleText>
            </PolicySectionTitle>
            <CreditCardsContainer>
              {paymentTypes.map((paymentType: PaymentTypeCode) => (
                <Card key={paymentType} $id={paymentType}>
                  {isSupportedCardType(paymentType) ? (
                    <img
                      src={`${CONTENT_HUB_UPLOAD_URL}/v1753798404/DAM/Artwork/Other/Icons/payment-methods/payment-positive-${getCardIconName(paymentType)}.svg`}
                      alt={`${getCardIconName(paymentType)} payment card`}
                    />
                  ) : (
                    <Text variant={descriptionVariant}>
                      {paymentType === 'CASH' ? t('vacation.select.policies.payment.cash_accepted') : paymentType}
                    </Text>
                  )}
                </Card>
              ))}
            </CreditCardsContainer>
          </PolicySection>
        )}
      </PoliciesContainer>
    </HotelPageContentSection>
  );
}

function PolicySectionCheckinCheckoutInfo({
  checkInformation,
  date,
  descriptionVariant,
}: {
  // travel-api can answer with no accommodation at all, in which case the adapter leaves both
  // check informations undefined while the trip dates still make the row worth rendering.
  checkInformation: CheckInformation | undefined;
  date: string | undefined;
  descriptionVariant: 'Body' | 'BodySmall';
}) {
  // CheckDateTime renders nothing when it has neither a date nor a time, so no pre-check here.
  // Gating on `date` alone used to hide the hotel's own times on the hotel-static page, which
  // never has trip dates, and left the row empty (MPH-1007).
  return (
    <>
      <CheckDateTime from={checkInformation?.from} to={checkInformation?.to} date={date} variant={descriptionVariant} />
      {checkInformation?.instructions?.map((instruction) => (
        <Text variant={descriptionVariant} key={instruction}>
          {instruction}
        </Text>
      ))}
    </>
  );
}

function getLanguageName(displayNames: Intl.DisplayNames, code: string) {
  const name = displayNames.of(code);
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : code;
}
